import { theme } from 'antd';
import { EditIcon } from 'assets/images/svg';
import { DefaultCategoryIcon } from 'assets/images/svg/Categories';
import { defaultCategory } from 'config/defaultCategory';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { categoriesActions } from 'reduxStore/categories/slice';
import { Avatar, Button, Flex, Input, Typography } from 'zui/Antd';

const { Text } = Typography;
const { TextArea } = Input;

const EditDrawerHeader = ({ categoryID, name, description, defaultCategoryID }) => {
	//Hooks
	const dispatch = useDispatch();
	const inputRef = useRef(null);
	const formColRef = useRef(null);
	const {
		token: {
			paddingLG,
			fontSizeHeading3,
			fontSizeHeading5,
			colorText,
			colorTextDescription,
			colorTextLabel,
			fontWeightStrong,
			fontSizeSM,
			colorError
		}
	} = theme.useToken();

	//Local state
	const [editable, setEditable] = useState(false);
	const [updatedName, setUpdatedName] = useState(name);
	const [updatedDesc, setUpdatedDesc] = useState(description);
	const [errors, setErrors] = useState({ name: '', description: '' });
	const [isExpanded, setIsExpanded] = useState(false);

	// Get icon from defaultCategory object
	const CategoryIcon = defaultCategory[defaultCategoryID]?.icon || DefaultCategoryIcon;

	useEffect(() => {
		if (editable && inputRef.current) {
			inputRef.current.focus();
		}
	}, [editable]);

	const handleFinish = useCallback(() => {
		const trimmedName = updatedName?.trim();
		const trimmedDesc = updatedDesc?.trim();
		const newErrors = { name: '', description: '' };

		if (!trimmedName) newErrors.name = 'Name is required';
		if (!trimmedDesc) newErrors.description = 'Description is required';

		setErrors(newErrors);
		if (newErrors.name || newErrors.description) return;

		if (trimmedName !== name.trim() || trimmedDesc !== description.trim()) {
			dispatch(
				categoriesActions.updateCategory({
					categoryID,
					data: {
						name: trimmedName,
						description: trimmedDesc
					}
				})
			);
		}

		setEditable(false);
	}, [dispatch, categoryID, updatedName, updatedDesc, name, description]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			const clickedOutside = formColRef.current && !formColRef.current.contains(event.target);

			if (editable && clickedOutside) {
				handleFinish();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [editable, handleFinish]);

	return (
		<Flex gap={paddingLG}>
			<Avatar
				size={54}
				icon={<CategoryIcon color='white' style={{ width: 42, height: 42 }} />}
				style={{ backgroundColor: '#A39486', alignSelf: 'start', marginTop: 6 }}
				shape='square'
			/>

			<Flex ref={formColRef} vertical flex='1'>
				{editable ? (
					<>
						<Input
							ref={inputRef}
							value={updatedName}
							onChange={(e) => setUpdatedName(e.target.value)}
							onPressEnter={handleFinish}
							style={{
								fontSize: fontSizeHeading3,
								fontWeight: fontWeightStrong,
								lineHeight: '32px',
								padding: 0
							}}
						/>
						{errors.name && (
							<Text style={{ color: colorError, fontSize: fontSizeSM, fontWeight: fontWeightStrong }}>
								{errors.name}
							</Text>
						)}
					</>
				) : (
					<Text
						style={{
							fontSize: fontSizeHeading3,
							fontWeight: fontWeightStrong,
							lineHeight: '32px',
							color: colorText
						}}
					>
						{name}
					</Text>
				)}

				{editable ? (
					<>
						<TextArea
							value={updatedDesc}
							onChange={(e) => setUpdatedDesc(e.target.value)}
							onPressEnter={handleFinish}
							style={{
								fontSize: fontSizeHeading5,
								fontWeight: fontWeightStrong,
								color: colorTextDescription,
								lineHeight: '24px'
							}}
							maxLength={150}
							showCount={false}
						/>
						{errors.description && (
							<Text style={{ color: colorError, fontSize: fontSizeSM, fontWeight: fontWeightStrong }}>
								{errors.description}
							</Text>
						)}
						<div style={{ textAlign: 'right', marginTop: 4 }}>
							<Text
								type={updatedDesc.length >= 150 ? 'danger' : 'secondary'}
								style={{ fontSize: fontSizeSM }}
							>
								{updatedDesc.length}/150
								{updatedDesc.length === 150 && ' - Character limit reached'}
							</Text>
						</div>
					</>
				) : (
					<Flex align='center' gap={6} wrap='wrap'>
						<Text
							style={{
								fontSize: fontSizeHeading5,
								fontWeight: fontWeightStrong,
								color: colorTextLabel,
								lineHeight: '24px',
								marginBottom: 0
							}}
						>
							{isExpanded
								? updatedDesc
								: `${updatedDesc.slice(0, 70)}${updatedDesc.length > 70 ? '...' : ''}`}
							{updatedDesc.length > 70 && (
								<Button
									type='link'
									size='small'
									style={{ padding: 0, fontSize: fontSizeSM, height: 'auto', marginLeft: 6 }}
									onClick={() => setIsExpanded((prev) => !prev)}
								>
									{isExpanded ? 'Collapse' : 'Expand'}
								</Button>
							)}
						</Text>
					</Flex>
				)}
			</Flex>

			<Button
				onClick={() => setEditable((prev) => !prev)}
				type='text'
				icon={<EditIcon />}
				style={{ alignSelf: 'start' }}
			/>
		</Flex>
	);
};

export default EditDrawerHeader;
